// Plan 23a, Task 5.2 — HP Bar primitive
// Ghost-drain effect is delegated to the Plan 20a hp-bar-chip hook.

import type { UIComponentHandle, UIComponentConfig } from './types';
import { createHpBarChipHook } from '../vfx/hp-bar-chip';

// ─────────────────────────────────────────────────────────────────────────────
// Tokens (from docs/design/tokens.json)
// ─────────────────────────────────────────────────────────────────────────────
const TOKEN = {
  /** color.hud.hp-bg */
  HP_BG: '#3A2020',
  /** color.hud.hp-fill */
  HP_FILL: '#E04040',
  /** color.hud.hp-ghost */
  HP_GHOST: 'rgba(224, 64, 64, 0.50)',
  /** overheal / overshield layer */
  HP_OVERSHIELD: 'rgba(176, 184, 200, 0.78)',
  /** color.hud.hp-low-pulse */
  HP_LOW_PULSE: '#FF6060',
  /** border-radius.xs */
  BORDER_RADIUS_XS: '4px',
  /** animation.duration.idle-pulse */
  IDLE_PULSE_DURATION: '2000ms',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Height map (from components.md §4 HP Bar — Height Variants)
// ─────────────────────────────────────────────────────────────────────────────
const HEIGHT_MAP: Record<NonNullable<HPBarOpts['height']>, string> = {
  hud: '6px',
  scoreboard: '8px',
  detail: '16px',
};

// ─────────────────────────────────────────────────────────────────────────────
// CSS injection (once per page)
// ─────────────────────────────────────────────────────────────────────────────

const HP_BAR_STYLE_ATTR = 'data-component';
const HP_BAR_STYLE_VALUE = 'hp-bar';

function injectStyles(): void {
  if (document.querySelector(`[${HP_BAR_STYLE_ATTR}="${HP_BAR_STYLE_VALUE}"]`)) return;

  const style = document.createElement('style');
  style.setAttribute(HP_BAR_STYLE_ATTR, HP_BAR_STYLE_VALUE);
  style.textContent = `
/* HP Bar — low-HP pulse animation (opacity 0.6 → 1.0 at 2s) */
@keyframes hp-bar-low-pulse {
  0%   { opacity: 1.0; }
  50%  { opacity: 0.6; }
  100% { opacity: 1.0; }
}

[data-hp-bar][data-hp-low="true"] [data-hp-fill] {
  background-color: ${TOKEN.HP_LOW_PULSE};
  animation: hp-bar-low-pulse ${TOKEN.IDLE_PULSE_DURATION} ease-in-out infinite;
}

/* HP Bar — damage flash (red shadow on container) */
[data-hp-bar][data-hp-flash="damage"] {
  box-shadow: 0 0 8px 2px rgba(255, 40, 40, 0.75);
}

/* HP Bar — heal flash (green overlay via outline) */
[data-hp-bar][data-hp-flash="heal"] {
  box-shadow: 0 0 8px 2px rgba(68, 255, 68, 0.45);
}
`.trim();

  document.head.appendChild(style);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface HPBarOpts {
  /** Current HP value (non-negative). */
  currentHP: number;
  /** Maximum HP value (must be > 0). */
  maxHP: number;
  /**
   * Height variant:
   *   - 'hud'        → 6px  (default)
   *   - 'scoreboard' → 8px
   *   - 'detail'     → 16px
   */
  height?: 'hud' | 'scoreboard' | 'detail';
  /** Render a centered "current/max" label inside the bar. */
  showValue?: boolean;
  /** Render HP above max as a grey overshield layer over the capped red base bar. */
  showOvershield?: boolean;
}

export interface HPBarHandle extends UIComponentHandle {
  /**
   * Update the HP bar. Delegates ghost-drain / heal / low-state to the
   * hp-bar-chip hook. The hook reads `[data-hp-ghost]` and `[data-hp-fill]`
   * inside the container.
   *
   * @param current - New HP value.
   * @param max     - Max HP (used to recompute percent).
   */
  setHP(current: number, max: number): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createHPBar(config: UIComponentConfig & HPBarOpts): HPBarHandle {
  const {
    gsapRegistry,
    mount,
    currentHP,
    maxHP,
    height = 'hud',
    showValue = false,
    showOvershield = false,
  } = config;

  injectStyles();

  // ── Internal state ─────────────────────────────────────────────────────────
  let disposed = false;
  let currentValue = currentHP;
  let currentPercent = hpBasePercent(currentHP, maxHP);

  // ── Create hp-bar-chip hook ────────────────────────────────────────────────
  const chip = createHpBarChipHook({ gsapRegistry });

  // ── Build DOM ──────────────────────────────────────────────────────────────

  // Container
  const container = document.createElement('div');
  container.className = 'ui-hp-bar';
  container.setAttribute('data-hp-bar', '');
  container.style.cssText = [
    `background-color: ${TOKEN.HP_BG}`,
    `border-radius: ${TOKEN.BORDER_RADIUS_XS}`,
    `width: 100%`,
    `height: ${HEIGHT_MAP[height]}`,
    `position: relative`,
    `overflow: hidden`,
    `box-sizing: border-box`,
  ].join('; ');

  // Ghost bar — rendered behind fill (order matters: first child = behind)
  const ghost = document.createElement('div');
  ghost.className = 'ui-hp-bar__ghost';
  ghost.setAttribute('data-hp-ghost', '');
  ghost.style.cssText = [
    `background-color: ${TOKEN.HP_GHOST}`,
    `border-radius: ${TOKEN.BORDER_RADIUS_XS}`,
    `position: absolute`,
    `top: 0`,
    `left: 0`,
    `height: 100%`,
    `width: ${currentPercent}%`,
    `pointer-events: none`,
  ].join('; ');

  // Fill bar — rendered in front of ghost (second child)
  const fill = document.createElement('div');
  fill.className = 'ui-hp-bar__fill';
  fill.setAttribute('data-hp-fill', '');
  fill.style.cssText = [
    `background-color: ${TOKEN.HP_FILL}`,
    `border-radius: ${TOKEN.BORDER_RADIUS_XS}`,
    `position: absolute`,
    `top: 0`,
    `left: 0`,
    `height: 100%`,
    `width: ${currentPercent}%`,
    `z-index: 1`,
    `pointer-events: none`,
  ].join('; ');

  // Ghost FIRST (z-order behind fill), fill SECOND
  container.appendChild(ghost);
  container.appendChild(fill);

  const overshield = document.createElement('div');
  if (showOvershield) {
    overshield.className = 'ui-hp-bar__overshield';
    overshield.setAttribute('data-hp-overshield', '');
    overshield.style.cssText = [
      `background-color: ${TOKEN.HP_OVERSHIELD}`,
      `border-radius: ${TOKEN.BORDER_RADIUS_XS}`,
      `position: absolute`,
      `top: 0`,
      `left: 0`,
      `height: 100%`,
      `width: ${hpOvershieldPercent(currentHP, maxHP)}%`,
      `display: ${currentHP > maxHP ? 'block' : 'none'}`,
      `z-index: 2`,
      `pointer-events: none`,
    ].join('; ');
    container.appendChild(overshield);
  }

  const valueLabel = document.createElement('span');
  if (showValue) {
    valueLabel.className = 'ui-hp-bar__label';
    valueLabel.setAttribute('data-hp-label', '');
    valueLabel.textContent = formatHPValue(currentHP, maxHP);
    valueLabel.style.cssText = [
      `position: absolute`,
      `inset: 0`,
      `z-index: 3`,
      `display: flex`,
      `align-items: center`,
      `justify-content: center`,
      `font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', sans-serif)`,
      `font-size: 11px`,
      `font-weight: 800`,
      `line-height: 1`,
      `color: #FFFFFF`,
      `text-shadow: 0 1px 2px rgba(0,0,0,0.85), 0 0 4px rgba(0,0,0,0.65)`,
      `white-space: nowrap`,
      `pointer-events: none`,
    ].join('; ');
    container.appendChild(valueLabel);
  }

  // ── Apply initial low-state attribute ──────────────────────────────────────
  applyLowState(container, currentPercent);

  // ── Mount ──────────────────────────────────────────────────────────────────
  if (mount) {
    mount.appendChild(container);
  }

  // ── setHP ──────────────────────────────────────────────────────────────────

  function setHP(current: number, max: number): void {
    if (disposed) return;

    const oldPercent = currentPercent;
    const oldValue = currentValue;
    const newPercent = hpBasePercent(current, max);
    const isHeal = current > oldValue;

    currentValue = current;
    currentPercent = newPercent;
    fill.style.width = `${newPercent}%`;
    if (showValue) {
      valueLabel.textContent = formatHPValue(current, max);
    }
    if (showOvershield) {
      overshield.style.width = `${hpOvershieldPercent(current, max)}%`;
      overshield.style.display = current > max ? 'block' : 'none';
    }

    // Delegate ghost-drain / heal flash / low state to the chip hook.
    // The hook reads [data-hp-ghost] and [data-hp-fill] inside the container.
    chip.play(container, {
      newHpPercent: newPercent,
      oldHpPercent: oldPercent,
      isHeal,
    });

    // Low state attribute — chip hook also handles this, but we set it
    // synchronously here so that tests can assert without needing to
    // advance the gsap registry timeline.
    applyLowState(container, newPercent);
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    chip.dispose();

    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }

  return {
    element: container,
    setHP,
    dispose,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function formatHPValue(current: number, max: number): string {
  const maxDisplay = Math.max(0, Math.round(max));
  const currentDisplay = Math.max(0, Math.round(current));
  return `${currentDisplay}/${maxDisplay}`;
}

function hpBasePercent(current: number, max: number): number {
  return clamp((Math.min(current, max) / max) * 100, 0, 100);
}

function hpOvershieldPercent(current: number, max: number): number {
  return clamp(((current - max) / max) * 100, 0, 100);
}

/**
 * Set/remove `data-hp-low="true"` on the container based on whether the
 * current HP percent is below the 30% threshold. CSS handles the pulse.
 * The chip hook also sets this attribute, but we mirror it synchronously
 * here so that the component's contract is self-contained.
 */
function applyLowState(container: HTMLElement, hpPercent: number): void {
  if (hpPercent < 30) {
    container.setAttribute('data-hp-low', 'true');
  } else {
    container.removeAttribute('data-hp-low');
  }
}
